import { createFileRoute } from "@tanstack/react-router";
import type { ClientAutoRender } from "api-types/autoRenders/autoRendersResponses";
import type { ClientRenderStyle } from "api-types/autoRenders/styles/autoRenderStylesResponses";
import { downloadResultUrl, getStyleNameForRender } from "lib/auto-renders";
import AutoRendersStyleCard from "lib/components/autoRenders/AutoRendersStyleCard";
import RenderStyleEditorModal from "lib/components/autoRenders/RenderStyleEditorModal";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import PlanList from "lib/components/builders/communities/plans/PlanList";
import PlanCard from "lib/components/builders/plan_cards/PlanCard";
import { DEFAULT_ELEVATION_URL } from "lib/components/builders/PlanThumbnail";
import {
  getBuilderAutoRendersURL,
  getBuilderAutoRenderStylesURL,
  getBuilderAutoRenderURL,
  getBuilderAutoRenderStyleURL,
} from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { userCanCreateAutoRenders } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import { deleteWithTimeout } from "lib/utils/fetchAuthHelpers";
import { useMemo, useState, type ReactNode } from "react";
import Button from "ui/buttons/Button";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import OverflowMenu from "ui/popovers/OverflowMenu";
import type { Column, DataTableRow } from "ui/tables/DataTable/types";
import withAuthentication from "../../../../components/withAuthentication";
import styles from "./index.module.scss";

function AutoRendersPage() {
  const { builderId } = useAppRouterParams();
  const { hasPermission, builder, enabledFeatures } = useUserContext();
  const canCreateAutoRenders = userCanCreateAutoRenders({ hasPermission, builder, enabledFeatures });
  const [deleteRenderId, setDeleteRenderId] = useState<string | null>(null);
  const [deleteStyleId, setDeleteStyleId] = useState<string | null>(null);
  const [, setIsDeletingRender] = useState(false);
  const [isDeletingStyle, setIsDeletingStyle] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [styleEditorOpen, setStyleEditorOpen] = useState(false);
  const [styleEditorMode, setStyleEditorMode] = useState<"create" | "edit">("create");
  const [styleBeingEdited, setStyleBeingEdited] = useState<ClientRenderStyle | null>(null);
  const { tab, menuItems: allMenuItems } = useTabbedMenu(["history", "environments"], {
    prettyNames: { environments: "Styling", history: "History" },
  });
  const menuItems = allMenuItems.filter((item) => item.path !== "#files");

  const { data: renderStyles, mutate: mutateRenderStyles } = useFetch<ClientRenderStyle[]>(
    builderId ? getBuilderAutoRenderStylesURL({ builderId }) : "",
    [builderId],
  );

  const {
    data: autoRendersData,
    loading: autoRendersLoading,
    mutate: mutateAutoRenders,
  } = useFetch<{
    data: ClientAutoRender[];
    totalCount: number;
  }>(builderId ? getBuilderAutoRendersURL({ builderId }) : "", [builderId]);

  const autoRenders = autoRendersData?.data || [];

  const renderStyleTableColumns: Column<ClientRenderStyle>[] = useMemo(
    () => [
      {
        header: "Name",
        accessor: function NameCell(style) {
          return style.name;
        },
      },
      {
        header: "Description",
        accessor: function DescriptionCell(style) {
          return style.description || "-";
        },
      },
      {
        header: "Type",
        accessor: function TypeCell(style) {
          return style.isSystemTemplate ? "System Template" : "Custom";
        },
      },
      {
        header: "Created",
        accessor: function CreatedCell(style) {
          return new Date(style.createdAt).toLocaleDateString();
        },
      },
      {
        id: "actions",
        accessor: function ActionsCell(style) {
          const menuItems: { children: ReactNode; onClick?: () => void }[] = [];

          // Only show edit/delete options for non-system templates when create permission is available
          if (canCreateAutoRenders && !style.isSystemTemplate) {
            menuItems.push(
              { children: "Delete", onClick: () => setDeleteStyleId(style.id) },
              {
                children: "Edit",
                onClick: () => {
                  setStyleEditorMode("edit");
                  setStyleBeingEdited(style);
                  setStyleEditorOpen(true);
                },
              },
            );
            return <OverflowMenu menuItems={menuItems} />;
          }
          return <span className={styles.systemTemplateDisabled}>default template</span>;
        },
      },
    ],
    [canCreateAutoRenders],
  );

  const renderStyleGridCard = (style: ClientRenderStyle): ReactNode => {
    const onEdit =
      canCreateAutoRenders && !style.isSystemTemplate
        ? (s) => {
            setStyleEditorMode("edit");
            setStyleBeingEdited(s);
            setStyleEditorOpen(true);
          }
        : undefined;
    const onDelete =
      canCreateAutoRenders && !style.isSystemTemplate
        ? () => {
            if (renderStyles && mutateRenderStyles) {
              void mutateRenderStyles(
                renderStyles.filter((s) => s.id !== style.id),
                false,
              );
            }
          }
        : undefined;
    return (
      <AutoRendersStyleCard
        style={style}
        onEdit={onEdit}
        onDelete={onDelete}
        onRefreshStyles={() => {
          if (mutateRenderStyles) {
            void mutateRenderStyles();
          }
        }}
        builderId={builderId}
      />
    );
  };

  const renderStyleOverflowMenu: DataTableRow<ClientRenderStyle>["overflowMenu"] = () => null;

  const autoRenderTableColumns: Column<ClientAutoRender>[] = useMemo(
    () => [
      {
        // Unnamed thumbnail column
        cell: function ThumbnailCell({ row }) {
          const render = row.original;
          const thumbnailUrl = render.resultUrl || render.screenshotUrl || DEFAULT_ELEVATION_URL;
          return (
            <div style={{ width: 150, height: 100 }}>
              <img
                src={thumbnailUrl}
                alt="Render thumbnail"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }}
              />
            </div>
          );
        },
        enableResizing: false,
        size: 150,
        fullCell: true,
        id: "thumbnail",
      },
      {
        header: "Name",
        accessor: function PlanNameCell(render) {
          return render.name || "-";
        },
      },
      {
        header: "Style",
        accessor: (render) => getStyleNameForRender(render, renderStyles),
      },
      {
        header: "Created By",
        accessor: function CreatedByCell(render) {
          // TODO: Fetch and display user name
          // For now, show "-" if no userId
          if (!render.userId) {
            return "-";
          }
          // TODO: Once user fetching is implemented, return user name here
          return "-";
        },
      },
      {
        header: "Aspect Ratio",
        accessor: function AspectRatioCell(render) {
          return render.aspectRatio;
        },
      },
      {
        header: "Date Created",
        accessor: function DateCreatedCell(render) {
          return new Date(render.createdAt).toLocaleDateString();
        },
      },
      {
        id: "actions",
        accessor: function ActionsCell(render) {
          return (
            <Button
              btnSize="small"
              btnStyle="primary"
              onClick={() => render.resultUrl && downloadResultUrl(render.resultUrl, `auto-render-${render.id}.png`)}
              disabled={!render.resultUrl || render.status !== "complete"}
            >
              Download
            </Button>
          );
        },
      },
      {
        id: "menu",
        accessor: function MenuCell(item) {
          if (!canCreateAutoRenders) {
            return null;
          }

          return (
            <OverflowMenu
              menuItems={[
                {
                  children: "Delete",
                  onClick: () => {
                    setDeleteRenderId(item.id);
                  },
                },
              ]}
            />
          );
        },
        enableResizing: false,
        size: 0,
        fullCell: false,
      },
    ],
    [canCreateAutoRenders, renderStyles],
  );

  const autoRenderGridCard = (render: ClientAutoRender): ReactNode => {
    const thumbnailUrl = render.resultUrl || render.screenshotUrl || DEFAULT_ELEVATION_URL;
    const styleName = getStyleNameForRender(render, renderStyles);

    return (
      <PlanCard imgUrl={thumbnailUrl} href="#" imageWidth={300}>
        <div className="plan-card-row">
          <div className="plan-card-cell plan-card-name">
            <div className="plan-card-name-text">{render.name || render.itemId}</div>
            <div className="plan-card-cell-label">
              {styleName} | {render.aspectRatio} | {new Date(render.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="plan-card-row">
          <div className="plan-card-cell cta center">
            <Button
              btnSize="small"
              btnStyle="primary"
              fullWidth
              onClick={() => render.resultUrl && downloadResultUrl(render.resultUrl, `auto-render-${render.id}.png`)}
              disabled={!render.resultUrl || render.status !== "complete"}
            >
              Download
            </Button>
          </div>
        </div>
      </PlanCard>
    );
  };

  const autoRenderOverflowMenu: DataTableRow<ClientAutoRender>["overflowMenu"] = () => {
    // No overflow menu needed for history
    return null;
  };

  return (
    <>
      {canCreateAutoRenders && deleteStyleId && builderId && (
        <ConfirmDeleteModal
          onClose={() => {
            setDeleteStyleId(null);
            setIsDeletingStyle(false);
          }}
          onConfirm={async (e) => {
            e.preventDefault();
            if (!deleteStyleId || !builderId) {
              return;
            }

            setIsDeletingStyle(true);
            try {
              const url = getBuilderAutoRenderStyleURL({ builderId, styleId: deleteStyleId });
              const res = await deleteWithTimeout(url, null, 30000);

              if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to delete style");
              }

              // Remove the deleted style from the list
              if (renderStyles) {
                void mutateRenderStyles(renderStyles.filter((s) => s.id !== deleteStyleId));
              }

              // Clear selection if the deleted style was selected
              if (selectedTemplateId === deleteStyleId) {
                setSelectedTemplateId(null);
              }

              setDeleteStyleId(null);
            } catch (error) {
              console.error("Failed to delete style:", error);
              // Error is handled by global error handling
            } finally {
              setIsDeletingStyle(false);
            }
          }}
          entityType="Render Style"
          entityName={renderStyles?.find((s) => s.id === deleteStyleId)?.name}
          message="Are you sure you want to delete this render style? This action cannot be undone."
          isDeleting={isDeletingStyle}
          requireEntityNameEntry={false}
        />
      )}
      {canCreateAutoRenders && deleteRenderId && (
        <ConfirmDeleteModal
          onClose={() => {
            setDeleteRenderId(null);
            setIsDeletingRender(false);
          }}
          onConfirm={async (e) => {
            e.preventDefault();
            if (!builderId || !deleteRenderId) {
              return;
            }

            setIsDeletingRender(true);

            try {
              const url = getBuilderAutoRenderURL({ builderId, autoRenderId: deleteRenderId });
              const res = await deleteWithTimeout(url, null, 30000);
              if (!res.ok) {
                throw new Error("Failed to delete render");
              }

              setDeleteRenderId(null);

              if (mutateAutoRenders) {
                void mutateAutoRenders();
              }
            } catch (error) {
              console.error("Failed to delete render:", error);
            } finally {
              setIsDeletingRender(false);
            }
          }}
          entityType="Render"
          message="Are you sure you want to delete this render? This action cannot be undone."
          entityName={autoRenders.find((render) => render.id === deleteRenderId)?.itemId}
        />
      )}
      <BuilderPortalLayout
        header="AutoRender"
        pageTitle="AutoRender"
        selected="autoRenders"
        menuItems={menuItems}
        loadingContent={autoRendersLoading}
      >
        <div className="builder-portal-layout-content">
          {tab === "environments" && (
            <PlanList
              plans={renderStyles || []}
              planTableColumns={renderStyleTableColumns}
              renderGridCard={renderStyleGridCard}
              overflowMenu={renderStyleOverflowMenu}
              sectionHeaderProps={{
                title: "Environments",
                headerButton: canCreateAutoRenders ? (
                  <Button
                    btnSize="small"
                    btnStyle="primary"
                    onClick={() => {
                      setStyleEditorMode("create");
                      setStyleEditorOpen(true);
                    }}
                  >
                    Create Style
                  </Button>
                ) : null,
              }}
              searchable
              searchKey="name"
            />
          )}
          {tab === "history" && (
            <PlanList
              plans={autoRenders}
              planTableColumns={autoRenderTableColumns}
              renderGridCard={autoRenderGridCard}
              overflowMenu={autoRenderOverflowMenu}
              sectionHeaderProps={{
                title: "Generated Renders",
                headerButton: null,
              }}
              searchable
              searchKey="itemId"
            />
          )}
        </div>
      </BuilderPortalLayout>
      {builderId && (
        <RenderStyleEditorModal
          open={styleEditorOpen}
          onClose={() => setStyleEditorOpen(false)}
          builderId={builderId}
          mode={styleEditorMode}
          style={styleBeingEdited ?? undefined}
          onSaved={(savedStyle) => {
            if (renderStyles && mutateRenderStyles) {
              const existingIndex = renderStyles.findIndex((s) => s.id === savedStyle.id);
              if (existingIndex >= 0) {
                const next = [...renderStyles];
                next[existingIndex] = savedStyle;
                void mutateRenderStyles(next, false);
              } else {
                void mutateRenderStyles([...renderStyles, savedStyle], false);
              }
            } else {
              console.error("Failed to save style: renderStyles or mutateRenderStyles is undefined");
            }
          }}
        />
      )}
    </>
  );
}

export const Route = createFileRoute("/builders/$builder_id/auto-renders/")({
  component: withAuthentication(AutoRendersPage, {
    permissions: ["VIEW_AUTO_RENDERS"],
    features: ["autoRenders"],
    products: ["autoRenders"],
  }),
});
